import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { displayWithCurrency } from '../../../utility/currency'
import { setSelectedPackage } from '../../../reducers/community-settings/pricingSettingsReducer'
import Pagination from '../../common/Pagination'
import Loader from '../../common/loader/legacy-loaders/Op36Loader-web'
import TooltipText from '../../common/TooltipText'
import Tooltip from '../../common/Tooltip'
import DefaultMultiActionsPopover from '../../common/PageHeader/DefaultMultiActionsPopover'

import './PackagesTable.scss'
import { ReactComponent as EllipsisIcon } from '../../../assets/images/common/icons/ellipsis-icon.svg'
import { ReactComponent as InfoCircleIcon } from '../../../assets/images/common/icons/info-circle-icon.svg'

const PackagesTable = ({
  fetchPackages,
  toggleArchiveConfirmationModal,
  toggleEditDisabledModal,
  toggleDeleteConfirmationModal,
  showPopover,
}) => {
  const dispatch = useDispatch()

  const { totalPages, packages, archived, isLoadingPackages, page } = useSelector(state => state.communitySettings.pricing)
  const currency = useSelector(state => state.user.current_user.communities[0].currency)

  const onEditPackage = pkg => {
    if (pkg.enrollmentCount) {
      toggleEditDisabledModal(true)
    } else {
      dispatch(setSelectedPackage(pkg))
    }
  }

  return (
    <div id='PackagesTable'>
      {totalPages > 1 && (
        <Pagination
          pageCount={totalPages}
          fetchData={fetchPackages}
          activePage={page}
        />
      )}
      {isLoadingPackages
        ? <Loader message={'loading packages'} />
        : packages && packages.length > 0
          ? (
            <div>
              {archived && (
                <Tooltip id='archivedTooltip'>
                  <div>
                    This pricing package has been archived. To ensure clean and reliable reporting (now and in the future),&nbsp;
                    archived packages with associated enrollments can not be unarchived. Thank you for understanding.
                  </div>
                </Tooltip>
              )}
              <table className='table table-responsive w-100'>
                <thead className='primary-background'>
                  <tr>
                    <th style={{ paddingLeft: '36px' }}>Name</th>
                    <th>Price</th>
                    <th>Description</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Associated programs</th>
                    <th>Enrollments</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Associated series</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Series Enrollments</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{packages.map((pkg) =>
                  <tr
                    key={pkg.id}
                    className={'packages-table-row'}
                  >
                    <td className='table-row-name'>
                      <TooltipText text={pkg.name} />
                    </td>
                    <td className='table-row-price'>{displayWithCurrency(pkg.price, currency, true)}</td>
                    <td className='table-row-description'>
                      <TooltipText text={pkg.description} />
                    </td>
                    <td style={{ width: '10%' }}>{pkg.courseCount}</td>
                    <td style={{ width: '10%' }}>{pkg.enrollmentCount}</td>
                    <td style={{ width: '10%' }}>{pkg.seriesCourseCount}</td>
                    <td style={{ width: '10%' }}>{pkg.seriesEnrollmentCount}</td>
                    <td className='table-row-actions' style={{ width: '5%' }}>
                      {archived ? (
                        <InfoCircleIcon className='info-circle-icon' data-for='archivedTooltip' data-tip />
                      ) : (
                        <>
                          <EllipsisIcon id={`package_${pkg.id}`} className='table-ellipsis' />
                          {showPopover && (
                            <DefaultMultiActionsPopover
                              targetId={`package_${pkg.id}`}
                              actions={[
                                { label: 'Edit Package', onClick: () => onEditPackage(pkg) },
                                ...(!pkg.enrollmentCount && !pkg.seriesEnrollmentCount
                                  ? [{ label: 'Delete Package', color: 'red', onClick: () => toggleDeleteConfirmationModal(pkg) }]
                                  : [{ label: 'Archive Package', color: 'red', onClick: () => toggleArchiveConfirmationModal(pkg) }]),
                              ]}
                            />
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='no-packages d-flex justify-content-center'>
              <p className='mb-0'>{`No ${archived ? 'archived' : 'available'} packages`}</p>
            </div>
          )
      }
    </div>
  )
}

PackagesTable.propTypes = {
  fetchPackages: PropTypes.func.isRequired,
  toggleArchiveConfirmationModal: PropTypes.func.isRequired,
  toggleEditDisabledModal: PropTypes.func.isRequired,
  toggleDeleteConfirmationModal: PropTypes.func.isRequired,
  showPopover: PropTypes.bool.isRequired,
}

export default PackagesTable
